import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DotsSixVertical, MapPin, CaretLeft, CaretRight, Calendar } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { fmtDateWithDay } from "@/lib/dates";
import { FalseLeadDialog } from "@/components/FalseLeadDialog";
import { STAGES } from "@/lib/stages";

export default function LeadsBoard() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dragId, setDragId] = useState(null);
    const [falseLeadFor, setFalseLeadFor] = useState(null); // {id, prevStage}
    const scrollerRef = useRef(null);

    const load = () => {
        setLoading(true);
        api.get("/leads").then((r) => setLeads(r.data)).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const scrollBy = (delta) => scrollerRef.current?.scrollBy({ left: delta, behavior: "smooth" });

    const commitStage = async (id, stage, reason = null, prevStage = null) => {
        const body = { stage };
        if (reason !== null) body.false_lead_reason = reason;
        try {
            await api.patch(`/leads/${id}/stage`, body);
            toast.success(`Moved to ${stage.replace("_", " ")}`);
            setLeads((p) => p.map((l) => (l.id === id ? { ...l, stage, false_lead_reason: reason ?? l.false_lead_reason } : l)));
        } catch {
            toast.error("Update failed");
            if (prevStage) setLeads((p) => p.map((l) => (l.id === id ? { ...l, stage: prevStage } : l)));
        }
    };

    const onDragStart = (id) => setDragId(id);
    const onDragOver = (e) => e.preventDefault();
    const onDrop = (stage) => {
        if (!dragId) return;
        const lead = leads.find((l) => l.id === dragId);
        if (!lead || lead.stage === stage) { setDragId(null); return; }
        if (stage === "dropped") {
            setFalseLeadFor({ id: dragId, prevStage: lead.stage });
            setDragId(null);
            return;
        }
        // optimistic
        const prevStage = lead.stage;
        setLeads((p) => p.map((l) => (l.id === dragId ? { ...l, stage } : l)));
        commitStage(dragId, stage, null, prevStage);
        setDragId(null);
    };

    const onFalseLeadConfirm = (reason) => {
        if (!falseLeadFor) return;
        setLeads((p) => p.map((l) => (l.id === falseLeadFor.id ? { ...l, stage: "dropped" } : l)));
        commitStage(falseLeadFor.id, "dropped", reason, falseLeadFor.prevStage);
        setFalseLeadFor(null);
    };

    const grouped = STAGES.reduce((acc, s) => {
        acc[s.key] = leads.filter((l) => l.stage === s.key);
        return acc;
    }, {});

    return (
        <div className="space-y-5" data-testid="pipeline-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="overline text-muted-foreground">Pipeline</div>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black mt-2 leading-none">
                        Your CRM, in motion.
                    </h1>
                </div>
                <div className="flex items-center gap-3">
                    <span className="overline text-muted-foreground">{leads.length} leads · drag to update</span>
                    <div className="flex border border-border rounded-sm">
                        <button data-testid="kanban-scroll-left" onClick={() => scrollBy(-340)}
                            className="px-2 py-1.5 hover:bg-muted transition-colors">
                            <CaretLeft size={16} />
                        </button>
                        <button data-testid="kanban-scroll-right" onClick={() => scrollBy(340)}
                            className="px-2 py-1.5 hover:bg-muted transition-colors border-l border-border">
                            <CaretRight size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground">Loading board…</div>
            ) : (
                <div ref={scrollerRef} className="flex gap-4 overflow-x-auto pb-4 scroll-smooth">
                    {STAGES.map((s) => (
                        <div
                            key={s.key}
                            onDragOver={onDragOver}
                            onDrop={() => onDrop(s.key)}
                            data-testid={`column-${s.key}`}
                            className="bg-muted/60 border border-border rounded-sm p-3 w-80 flex-shrink-0 min-h-[400px]"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <span className={`h-2.5 w-2.5 ${s.color} rounded-sm`} />
                                    <span className="font-heading font-bold text-sm uppercase tracking-wider">{s.label}</span>
                                </div>
                                <span className="overline text-muted-foreground">{grouped[s.key]?.length || 0}</span>
                            </div>
                            <div className="space-y-2">
                                {grouped[s.key]?.length === 0 && (
                                    <div className="text-xs text-muted-foreground border border-dashed border-border rounded-sm p-3 text-center">
                                        Drop leads here
                                    </div>
                                )}
                                {grouped[s.key]?.map((l) => (
                                    <div
                                        key={l.id}
                                        draggable
                                        onDragStart={() => onDragStart(l.id)}
                                        data-testid={`card-${l.id}`}
                                        className={`bg-white border border-border rounded-sm p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${
                                            dragId === l.id ? "opacity-50" : ""
                                        }`}
                                    >
                                        <div className="flex items-start gap-2">
                                            <DotsSixVertical size={14} className="text-muted-foreground mt-1 shrink-0" />
                                            <div className="min-w-0 flex-1">
                                                <Link to={`/leads/${l.id}`} className="font-medium hover:text-brand block truncate">
                                                    {l.company_name}
                                                </Link>
                                                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 truncate">
                                                    <MapPin size={11} /> {l.location || "—"}
                                                </div>
                                                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                                                    <span className="chip border-border bg-muted text-muted-foreground">
                                                        {l.industry}
                                                    </span>
                                                    {l.insights?.lead_score != null && (
                                                        <span className="chip border-signal-ai text-signal-ai">
                                                            {l.insights.lead_score}
                                                        </span>
                                                    )}
                                                </div>
                                                {l.next_action_date && (
                                                    <div className="mt-2 text-[10px] text-muted-foreground inline-flex items-center gap-1">
                                                        <Calendar size={11} /> Next: {fmtDateWithDay(l.next_action_date)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <FalseLeadDialog
                open={!!falseLeadFor}
                onClose={() => setFalseLeadFor(null)}
                onConfirm={onFalseLeadConfirm}
            />
        </div>
    );
}
